import React from 'react';
import { format } from 'date-fns';

interface BookingSummaryProps {
  clientName: string;
  date: Date;
  startTime: string;
  serviceName: string;
  serviceDuration: number;
  servicePrice: number;
  currency?: string;
}

const BookingSummary: React.FC<BookingSummaryProps> = ({
  clientName,
  date,
  startTime,
  serviceName,
  serviceDuration,
  servicePrice,
  currency = 'USD',
}) => {
  const formatPrice = (cents: number): string => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);
  };

  const formatDuration = (minutes: number): string => {
    if (minutes < 60) return `${minutes} minutes`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) return `${hours} hour${hours > 1 ? 's' : ''}`;
    return `${hours} hour${hours > 1 ? 's' : ''} ${mins} minutes`;
  };

  const calculateEndTime = (): string => {
    const [hours, minutes] = startTime.split(':').map(Number);
    const startDate = new Date(date);
    startDate.setHours(hours, minutes, 0, 0);

    const endDate = new Date(startDate);
    endDate.setMinutes(endDate.getMinutes() + serviceDuration);

    return format(endDate, 'HH:mm');
  };

  return (
    <div className="p-4 bg-purple-50 border border-purple-200 rounded-xl animate-fadeIn">
      <h3 className="text-base font-semibold text-purple-600 mb-3">Booking Summary</h3>

      <dl className="space-y-2">
        {/* Client */}
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-purple-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
          </svg>
          <dd className="text-sm text-gray-900 font-medium">{clientName}</dd>
        </div>

        {/* Date */}
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-purple-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <dd className="text-sm text-gray-900">{format(date, 'MMMM dd, yyyy')}</dd>
        </div>

        {/* Time */}
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-purple-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <dd className="text-sm text-gray-900">
            {startTime} - {calculateEndTime()}
          </dd>
        </div>

        {/* Service */}
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-purple-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
          <dd className="text-sm text-gray-900">{serviceName}</dd>
        </div>
      </dl>

      <div className="mt-4 pt-3 border-t border-purple-200">
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-gray-700">Duration:</span>
          <span className="text-sm font-semibold text-gray-900">{formatDuration(serviceDuration)}</span>
        </div>
        <div className="flex justify-between items-center mt-1">
          <span className="text-sm font-medium text-gray-700">Total:</span>
          <span className="text-base font-bold text-gray-900">{formatPrice(servicePrice)}</span>
        </div>
      </div>
    </div>
  );
};

export default BookingSummary;
